import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useSearchParams } from 'react-router-dom';
import { FiPlus, FiEdit, FiEye, FiFilter, FiCalendar, FiColumns, FiList } from 'react-icons/fi';
import visitService from '../../services/visitService';
import stageService from '../../services/stageService';
import api from '../../services/api';
import { VISIT_STATUS, VISIT_TYPES, STAGE_WORKFLOW, formatVisitId, dispatchNotificationsRefresh } from '../../utils/constants';
import { formatDateDDMMMYYYY } from '../../utils/dateFormat';
import { common as commonMessages, visits as visitMessages, actionItems as actionItemMessages } from '../../utils/validationMessages';
import { useProject } from '../../context/ProjectContext';
import { AuthContext } from '../../context/AuthContext';
import { useSession } from '../../context/SessionContext';
import Spinner from '../Common/Spinner';
import Alert from '../Common/Alert';
import VisitCalendar from './VisitCalendar';
import ScheduleVisitModal from './ScheduleVisitModal';
import ReportReviewModal from './ReportReviewModal';
import VisitActionsModal from './VisitActionsModal';
import ConfirmPIModal from './ConfirmPIModal';
import GenerateVisitLetterModal from './GenerateVisitLetterModal';
import GenerateFollowUpLetterModal from './GenerateFollowUpLetterModal';
import ConfirmFollowUpPIModal from './ConfirmFollowUpPIModal';
import ExecuteVisitModal from './ExecuteVisitModal';
import CreateActionItemModal from '../ActionItems/CreateActionItemModal';
import { ElectronicSign } from '../Common/ElectronicSign';
import { Modal } from '../Common/Modal';
import Pagination from '../Common/Pagination';
import CRFForm from '../CRF/CRFForm';
import '../Common/DataGrid.css';
import './VisitManagement.css';

/** Map dashboard / URL ?stage= value to internal activeStage id. */
function parseVisitsUrlStageParam(param) {
  if (param == null) return null;
  const p = String(param).trim();
  if (!p) return null;
  const lower = p.toLowerCase();
  if (lower === 'schedule') return 'schedule';
  if (lower === 'ongoing' || lower === 'inprogress' || lower === 'in_progress' || p === 'onGoing') return 'onGoing';
  if (lower === 'closed' || lower === 'completed') return 'closed';
  const n = parseInt(p, 10);
  if (!Number.isNaN(n)) return String(n);
  return null;
}

/** Same mapping as in-component stageCodeToId (for effects that run before inner function init). */
function stageCodeToActiveStageId(code) {
  if (code === STAGE_WORKFLOW.SCHEDULE) return 'schedule';
  if (code === STAGE_WORKFLOW.IN_PROGRESS) return 'onGoing';
  if (code === STAGE_WORKFLOW.COMPLETED) return 'closed';
  if (code > STAGE_WORKFLOW.IN_PROGRESS && code < STAGE_WORKFLOW.COMPLETED) return String(code);
  return null;
}

/**
 * Match search text within a single column only. Joining all columns with spaces caused false
 * positives (e.g. "Site Selection Visit 1" matching visit type + start date "11-Apr-2026").
 */
function visitRowMatchesSearch(v, qRaw) {
  const q = (qRaw || '').trim().toLowerCase();
  if (!q) return true;
  const visitId = formatVisitId(v);
  const visitType = (VISIT_TYPES[v.visitType] || v.visitType || '').toString();
  const plannedStart = v.plannedStartDate ? formatDateDDMMMYYYY(v.plannedStartDate) : '';
  const plannedEnd = v.plannedEndDate ? formatDateDDMMMYYYY(v.plannedEndDate) : '';
  const actualStart = v.actualStartDate ? formatDateDDMMMYYYY(v.actualStartDate) : '';
  const actualEnd = v.actualEndDate ? formatDateDDMMMYYYY(v.actualEndDate) : '';
  const status = (v.visitStatus ?? v.VisitStatus ?? '').toString();
  const site = (v.siteName ?? v.SiteName ?? '').toString();
  const siteCode = (v.vSiteCode ?? v.siteCode ?? v.SiteCode ?? '').toString();
  const fields = [visitId, visitType, plannedStart, plannedEnd, actualStart, actualEnd, status, site, siteCode];
  return fields.some((field) => String(field || '').toLowerCase().includes(q));
}

const VisitManagement = () => {
  const { user } = useContext(AuthContext);
  const { nRecordedBy: sessionRecordedByMappingId } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentProject: contextProject, updateProject: setCurrentProject } = useProject();
  const currentProject = contextProject && contextProject !== 'all' ? contextProject : null;
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeStage, setActiveStage] = useState('schedule');
  const [viewMode, setViewMode] = useState('calendar'); // 'panel' or 'calendar' - default to calendar for schedule
  const [filters, setFilters] = useState({
    site: 'all',
    cra: 'all',
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [selectedVisitForReview, setSelectedVisitForReview] = useState(null);
  const [isActionsModalOpen, setIsActionsModalOpen] = useState(false);
  const [selectedVisitForActions, setSelectedVisitForActions] = useState(null);
  const [isConfirmPIModalOpen, setIsConfirmPIModalOpen] = useState(false);
  const [visitForConfirmPI, setVisitForConfirmPI] = useState(null);
  const [isGenerateLetterModalOpen, setIsGenerateLetterModalOpen] = useState(false);
  const [visitForLetter, setVisitForLetter] = useState(null);
  const [isGenerateFollowUpLetterModalOpen, setIsGenerateFollowUpLetterModalOpen] = useState(false);
  const [visitForFollowUpLetter, setVisitForFollowUpLetter] = useState(null);
  const [isConfirmFollowUpPIModalOpen, setIsConfirmFollowUpPIModalOpen] = useState(false);
  const [visitForFollowUpConfirmPI, setVisitForFollowUpConfirmPI] = useState(null);
  const [isExecuteVisitModalOpen, setIsExecuteVisitModalOpen] = useState(false);
  const [visitForExecute, setVisitForExecute] = useState(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportTemplateId, setReportTemplateId] = useState(null);
  const [reportVisit, setReportVisit] = useState(null);
  const [visitBeingEdited, setVisitBeingEdited] = useState(null);
  const [reviewTab, setReviewTab] = useState('pending'); // 'pending' or 'actions' for review stages
  const [actionItems, setActionItems] = useState([]);
  const [actionItemClosingId, setActionItemClosingId] = useState(null);
  const [showActionItemCloseESign, setShowActionItemCloseESign] = useState(false);
  const [showActionItemReplyESign, setShowActionItemReplyESign] = useState(false);
  const [actionItemIdToClose, setActionItemIdToClose] = useState(null);
  const [issueWorkflowModalOpen, setIssueWorkflowModalOpen] = useState(false);
  const [issueWorkflowMode, setIssueWorkflowMode] = useState('reply'); // reply | view
  const [selectedIssueItem, setSelectedIssueItem] = useState(null);
  const [issueReplyRemark, setIssueReplyRemark] = useState('');
  /** Inline validation under Action Item Description (reply modal); not toaster */
  const [issueReplyRemarkFieldError, setIssueReplyRemarkFieldError] = useState('');
  /** null | 'update' (remark only) | 'finalize' (submit to creator after e-sign) */
  const [issueReplyBusyKind, setIssueReplyBusyKind] = useState(null);
  const [workflowStagesFromApi, setWorkflowStagesFromApi] = useState([]); // dynamic stages from GET /api/stage (profile-filtered)
  const [stageCountsByCode, setStageCountsByCode] = useState({}); // stageCode -> count from GET /api/visit/stage-counts
  const [stagesLoaded, setStagesLoaded] = useState(false); // true after first getStages response so we can show "no stages" vs loading
  const [isCreateActionItemModalOpen, setIsCreateActionItemModalOpen] = useState(false);
  const [issueDetailsModalOpen, setIssueDetailsModalOpen] = useState(false);
  const [selectedIssueForDetails, setSelectedIssueForDetails] = useState(null);
  const [sites, setSites] = useState([]);
  const [cras, setCras] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [visitsWithCRFDraft, setVisitsWithCRFDraft] = useState(() => new Set());
  const [loadingCRFDraftStatus, setLoadingCRFDraftStatus] = useState(false);
  const [profilePermissions, setProfilePermissions] = useState({});
  const [visitSearch, setVisitSearch] = useState('');
  const [visitPage, setVisitPage] = useState(1);
  const [visitPageSize, setVisitPageSize] = useState(10);
  const [actionItemPage, setActionItemPage] = useState(1);
  const [actionItemPageSize, setActionItemPageSize] = useState(10);
  const [actionItemSearch, setActionItemSearch] = useState('');
  const [toast, setToast] = useState(null);
  /** Set before opening reply e-sign: 'update' (remark only, Reply modal closes first) | 'finalize' (Save) */
  const issueReplyESignIntentRef = useRef(null);
  /** Supersedes in-flight GET /visit when stage/project changes (e.g. ?stage= runs after first fetch effect). */
  const visitsFetchGenerationRef = useRef(0);

  // Profile activity permissions: iScheduleModify, iVisitActivity, iStartReport, iApproveSendback; IsViewOnly hides all transactional actions
  useEffect(() => {
    let cancelled = false;
    api.get('/users/me/profile-permissions')
      .then((res) => {
        if (!cancelled && res.data) setProfilePermissions(res.data);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [currentProject]);

  const isViewOnly = profilePermissions.isViewOnly === 1 || profilePermissions.IsViewOnly === 1;
  const canSchedule = !isViewOnly && (profilePermissions.iScheduleModify === 1 || profilePermissions.iScheduleModify === '1');
  const canVisitActivity = !isViewOnly && (profilePermissions.iVisitActivity === 1 || profilePermissions.iVisitActivity === '1');
  const canStartReport = !isViewOnly && (profilePermissions.iStartReport === 1 || profilePermissions.iStartReport === '1');
  const canApproveReject = !isViewOnly && (profilePermissions.iApproveSendback === 1 || profilePermissions.iApproveSendback === '1');
  const currentUserId = Number(user?.iuid ?? user?.Iuid ?? user?.userId ?? user?.UserId ?? user?.id ?? 0) || 0;

  /** Resolved + session UserRoleProjectSiteMapping id matches createdBy + profile match for Close in View modal. */
  const canShowIssueCloseButton = (item) => {
    if (!item) return false;
    if ((item.status || '').toString().toLowerCase() !== 'resolved') return false;
    const sessionMid = Number(sessionRecordedByMappingId ?? 0) || 0;
    if (!sessionMid || Number(item.createdBy || 0) !== sessionMid) return false;
    const cur = (user?.profileCode ?? user?.vUserTypeCode ?? user?.role ?? '').toString().trim().toUpperCase();
    const raised = (item.createdByProfileCode ?? item.CreatedByProfileCode ?? '').toString().trim().toUpperCase();
    if (!raised) return true;
    return Boolean(cur && cur === raised);
  };

  useEffect(() => {
    const loadFilters = async () => {
      setLoadingFilters(true);
      try {
        // Fetch both sites and CRAs in parallel from database
        await Promise.all([
          fetchSites(),
          fetchCRAs()
        ]);
      } catch (err) {
        console.error('Error loading filters:', err);
      } finally {
        setLoadingFilters(false);
      }
    };
    loadFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject]); // Refetch sites when project changes

  useEffect(() => {
    fetchVisits();
  }, [filters, currentProject, activeStage]);

  useEffect(() => {
    setVisitPage(1);
    setActionItemPage(1);
    setActionItemSearch('');
  }, [activeStage, reviewTab]);

  // Fetch dynamic stages from API (profile-filtered by backend); refetch when project changes
  useEffect(() => {
    let cancelled = false;
    setStagesLoaded(false);
    stageService.getStages(currentProject || undefined)
      .then((list) => {
        if (!cancelled && Array.isArray(list)) {
          setWorkflowStagesFromApi(list);
          setStagesLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) setStagesLoaded(true);
      });
    return () => { cancelled = true; };
  }, [currentProject]);

  // Stage counts (project + profile): refetch on project change and after visit mutations (schedule, execute, etc.)
  const refetchStageCounts = useCallback(async () => {
    const projectCodeForApi = currentProject == null || currentProject === 'all'
      ? undefined
      : (typeof currentProject === 'string' ? currentProject : (currentProject?.projectCode ?? currentProject?.ProjectCode));
    try {
      const rows = await stageService.getVisitStageCounts(projectCodeForApi || undefined);
      const map = {};
      (Array.isArray(rows) ? rows : []).forEach((r) => {
        const code = r.stageCode ?? r.StageCode;
        const count = r.count ?? r.Count ?? 0;
        if (code != null) map[Number(code)] = Number(count) || 0;
      });
      setStageCountsByCode(map);
    } catch {
      setStageCountsByCode({});
    }
  }, [currentProject]);

  useEffect(() => {
    void refetchStageCounts();
  }, [refetchStageCounts]);

  // Fetch CRF action items by stageCode + projectCode (dynamic stages; no static stage names)
  useEffect(() => {
    const code = activeStage === 'onGoing' ? STAGE_WORKFLOW.IN_PROGRESS : (activeStage === 'schedule' || activeStage === 'closed' ? null : parseInt(activeStage, 10));
    if (code == null || isNaN(code)) {
      setActionItems([]);
      return;
    }
    refetchActionItems();
  }, [activeStage, filters, currentProject, sites, user?.profileCode, user?.vUserTypeCode, user?.role, sessionRecordedByMappingId]);

  // Load CRF draft status for In Progress visits so "Continue" shows instead of "Start Report"
  useEffect(() => {
    if (activeStage !== 'onGoing') {
      setLoadingCRFDraftStatus(false);
      return;
    }
    const filtered = (Array.isArray(visits) ? visits : []).filter((v) => {
      if (!v) return false;
      if (filters.site !== 'all' && (v.siteCode ?? v.SiteCode) !== filters.site) return false;
      if (filters.cra !== 'all' && getVisitCraFilterId(v) !== filters.cra) return false;
      return true;
    });
    const inProgressLabel = (() => {
      const code = STAGE_WORKFLOW.IN_PROGRESS;
      const s = workflowStagesFromApi.find((x) => (x.stageCode ?? x.StageCode) === code);
      return s ? (s.label ?? s.Label ?? s.vDisplayName ?? s.VDisplayName ?? '') : '';
    })();
    const onGoingVisits = filtered.filter((v) => {
      const crf = v.crfStage ?? v.CrfStage ?? '';
      const hasOpenDCF = v.hasOpenDCF ?? v.HasOpenDCF;
      if (hasOpenDCF) return true;
      if (crf !== '' && crf !== inProgressLabel) return false;
      return (v.visitStatus ?? v.VisitStatus) === VISIT_STATUS.ONGOING || (inProgressLabel && crf === inProgressLabel);
    });
    if (onGoingVisits.length === 0) {
      setLoadingCRFDraftStatus(false);
      return;
    }

    setLoadingCRFDraftStatus(true);
    let cancelled = false;
    const checkDrafts = async () => {
      const draftVisitIds = new Set();
      await Promise.all(
        onGoingVisits.map(async (visit) => {
          const vid = visit?.scheduledVisitID ?? visit?.scheduledVisitId ?? visit?.ScheduledVisitID;
          if (vid == null) return;
          if (visit.hasOpenDCF ?? visit.HasOpenDCF) {
            draftVisitIds.add(Number(vid));
            return;
          }
          try {
            const templateRes = await visitService.getAssignedCRFTemplate(vid);
            const templateId = templateRes?.templateId ?? templateRes?.TemplateId;
            if (!templateId) return;
            const hasRes = await api.get(`/crf/page/has-draft/${vid}/template/${templateId}`);
            const hasDraft = hasRes.data?.hasDraft ?? hasRes.data?.HasDraft;
            if (hasDraft) draftVisitIds.add(Number(vid));
          } catch (_) { /* ignore */ }
        })
      );
      if (!cancelled) {
        setVisitsWithCRFDraft((prev) => new Set([...prev, ...draftVisitIds]));
        setLoadingCRFDraftStatus(false);
      }
    };
    checkDrafts();
    return () => {
      cancelled = true;
      setLoadingCRFDraftStatus(false);
    };
  }, [activeStage, visits, filters, workflowStagesFromApi]);

  const fetchSites = async () => {
    try {
      // Fetch sites filtered by current project if available
      const url = currentProject && currentProject !== 'all'
        ? `/sites?project=${currentProject}`
        : '/sites';
      const response = await api.get(url);

      if (!response.data) {
        console.warn('No sites data received from API');
        setSites([]);
        return;
      }

      // Map sites: use SiteCode for filter value; display as vSiteCode + vSiteDescription
      const sitesList = (response.data || [])
        .map(site => {
          const siteCode = (site.siteCode ?? site.vSiteCode ?? site.sitedescription ?? '').toString().trim();
          const siteDescription = (site.sitedescription ?? site.siteDescription ?? site.SiteDescription ?? '').toString().trim();
          const siteDisplay = [siteCode, siteDescription].filter(Boolean).join(' - ') || siteCode || siteDescription;
          return {
            siteID: site.siteID,
            siteCode,
            siteName: site.sitedescription || site.siteCode || '',
            siteDescription,
            siteDisplay,
            projectCode: site.projectCode || null,
            piName: site.piName ?? site.investigator ?? '',
          };
        })
        .filter(site => site.siteCode) // Filter out empty site codes
        .sort((a, b) => a.siteCode.localeCompare(b.siteCode));

      setSites(sitesList);
      console.log(`Loaded ${sitesList.length} sites from database`);
    } catch (err) {
      console.error('Error fetching sites from database:', err);
      setError(err.response?.data?.message || 'Failed to load sites from database');
      setSites([]);
    }
  };

  const getVisitSiteDisplay = (visit, sitesList) => {
    if (!visit) return '—';
    const code = visit.vSiteCode ?? visit.siteCode ?? visit.SiteCode ?? '';
    let name = visit.siteName ?? visit.SiteName ?? '';
    if (!name) {
      const siteId = visit.siteID ?? visit.siteId ?? visit.SiteID;
      if (siteId != null && sitesList && sitesList.length > 0) {
        const site = sitesList.find((s) => String(s.siteID ?? s.siteId) === String(siteId));
        name = site?.siteDisplay ?? site?.siteName ?? site?.sitedescription ?? '';
      }
    }
    const codeStr = (code || '').toString().trim();
    const nameStr = (name || '').toString().trim();
    const combined = [codeStr, nameStr].filter(Boolean).join(' - ');
    return combined || codeStr || nameStr || '—';
  };

  /** Site label for issue rows: prefer staged visit row, else API snapshot (site when visit is not in current stage list). */
  const getActionItemSiteDisplay = (item, visit, sitesList) => {
    const fromVisit = getVisitSiteDisplay(visit, sitesList);
    if (fromVisit && fromVisit !== '—') return fromVisit;
    const code = (item.siteCode ?? item.SiteCode ?? '').toString().trim();
    const desc = (item.siteDescription ?? item.SiteDescription ?? '').toString().trim();
    const combined = [code, desc].filter(Boolean).join(' - ');
    if (combined) return combined;
    const sid = item.siteId ?? item.siteID ?? item.SiteID;
    if (sid != null && sitesList && sitesList.length > 0) {
      const site = sitesList.find((s) => String(s.siteID ?? s.siteId) === String(sid));
      const d = site?.siteDisplay ?? site?.siteName ?? '';
      if (d) return d;
    }
    return '—';
  };

  const getActionItemVisitTypeLabel = (item, visit) => {
    const code = (visit?.visitType ?? item.visitType ?? item.VisitType ?? '').toString().trim();
    if (!code) return '-';
    return VISIT_TYPES[code] || code;
  };

  /** Visit ID for issue row: same as visits grid (DisplayVisitCode or VisitType+ScheduledVisitID). */
  const getActionItemVisitIdDisplay = (item, visit) => {
    if (visit) return formatVisitId(visit);
    const dc = (item.displayVisitCode ?? item.DisplayVisitCode ?? '').toString().trim();
    if (dc) return dc;
    const vid = item.scheduledVisitID ?? item.ScheduledVisitID;
    const vt = (item.visitType ?? item.VisitType ?? 'SMV').toString().trim();
    if (vid != null && String(vid) !== '') return `${vt}${vid}`;
    return '—';
  };

  /** Case-insensitive match on any issue grid column (and common raw API fields). */
  const actionItemRowMatchesSearch = (item, visit, qRaw) => {
    const q = (qRaw || '').trim().toLowerCase();
    if (!q) return true;
    const refNo = String(item.actionItemNumber ?? item.vActionItemNumber ?? item.nCRFActionItemNo ?? '').toLowerCase();
    const visitIdDisp = String(getActionItemVisitIdDisplay(item, visit) ?? '').toLowerCase();
    const siteDisp = String(getActionItemSiteDisplay(item, visit, sites) ?? '').toLowerCase();
    const visitType = String(getActionItemVisitTypeLabel(item, visit) ?? '').toLowerCase();
    const issueOn = String(item.actionItemOn ?? '').toLowerCase();
    const finding = String(item.findingDescription ?? '').toLowerCase();
    const assigned = String(
      item.assignedToName || item.assignedToProfileCode || 'CRA',
    ).toLowerCase();
    const due = item.dueDate ? formatDateDDMMMYYYY(item.dueDate).toLowerCase() : '';
    const priority = String(item.priorityDescription ?? item.severity ?? '').toLowerCase();
    const status = String(item.status || 'Open').toLowerCase();
    const visitIdRaw = String(item.scheduledVisitID ?? item.ScheduledVisitID ?? '').toLowerCase();
    const displayCode = String(item.displayVisitCode ?? item.DisplayVisitCode ?? '').toLowerCase();
    const fields = [
      refNo,
      visitIdDisp,
      siteDisp,
      visitType,
      issueOn,
      finding,
      assigned,
      due,
      priority,
      status,
      visitIdRaw,
      displayCode,
    ];
    return fields.some((f) => f.includes(q));
  };

  /** Resolve PI (Investigator) from visit or from saved site when visit has no PI. */
  const getVisitPI = (visit, sitesList) => {
    if (!visit) return '';
    const fromVisit = visit.piName ?? visit.PIName ?? visit.pIName ?? '';
    if (fromVisit && String(fromVisit).trim()) return fromVisit.trim();
    const siteId = visit.siteID ?? visit.siteId ?? visit.SiteID;
    if (siteId != null && sitesList && sitesList.length > 0) {
      const site = sitesList.find((s) => String(s.siteID ?? s.siteId) === String(siteId));
      const fromSite = site?.piName ?? site?.investigator ?? '';
      if (fromSite && String(fromSite).trim()) return fromSite.trim();
    }
    return '';
  };

  const getVisitCraFilterId = (visit) => {
    if (!visit) return '';
    const craId = visit.cra_UserID ?? visit.CRA_UserID ?? visit.craID ?? visit.Cra_UserID ?? visit.CraUserID;
    return craId != null && String(craId).trim() !== '' ? String(craId) : '';
  };

  const fetchCRAs = async () => {
    try {
      const response = await api.get('/users/cras');

      if (!response.data) {
        console.warn('No CRAs data received from API');
        setCras([]);
        return;
      }

      // Map CRAs, dedupe by id (API validates vUserTypeCode; same user appears once)
      const raw = response.data || [];
      const seen = new Set();
      const crasList = raw
        .map(cra => ({ id: String(cra.id), name: cra.name || '' }))
        .filter(cra => cra.name && !seen.has(cra.id) && seen.add(cra.id))
        .sort((a, b) => a.name.localeCompare(b.name));

      setCras(crasList);
      console.log(`Loaded ${crasList.length} CRAs from database`);
    } catch (err) {
      console.error('Error fetching CRAs from database:', err);
      setError(err.response?.data?.message || 'Failed to load CRAs from database');
      setCras([]);
    }
  };

  const refetchActionItems = async () => {
    // Dynamic stage: use stageCode (101, 201, 9999 fixed; between 201 and 9999 study-specific). No static stage names.
    const stageCode = activeStage === 'onGoing' ? STAGE_WORKFLOW.IN_PROGRESS : (activeStage === 'schedule' || activeStage === 'closed' ? null : parseInt(activeStage, 10));
    if (stageCode == null || isNaN(stageCode) || !currentProject) {
      setActionItems([]);
      return;
    }
    const params = { stageCode, projectCode: currentProject };
    const viewerProfileCode = (user?.profileCode ?? user?.vUserTypeCode ?? user?.role ?? '').toString().trim().toUpperCase();
    if (viewerProfileCode) params.assignedToProfileCode = viewerProfileCode;
    if (filters.site && filters.site !== 'all') {
      const site = sites.find((s) => (s.siteCode ?? s.siteName) === filters.site);
      if (site && (site.siteID ?? site.siteId) != null) params.siteId = site.siteID ?? site.siteId;
    }
    try {
      const res = await api.get('/action-items', { params });
      const list = Array.isArray(res.data) ? res.data : (res.data?.data && Array.isArray(res.data.data) ? res.data.data : []);
      const sessionMid = Number(sessionRecordedByMappingId ?? 0) || 0;
      const viewerProf = (user?.profileCode ?? user?.vUserTypeCode ?? user?.role ?? '').toString().trim().toUpperCase();
      const visibleList = list.filter((x) => {
        const st = (x?.status || '').toString().trim().toLowerCase();
        if (st === 'closed') return false;
        if (st !== 'resolved') return true;
        if (sessionMid > 0 && Number(x?.createdBy ?? x?.CreatedBy ?? 0) === sessionMid) return true;
        const assigned = (x?.assignedToProfileCode ?? x?.AssignedToProfileCode ?? '').toString().trim().toUpperCase();
        if (viewerProf && assigned && assigned === viewerProf) return false;
        return true;
      });
      setActionItems(visibleList);
    } catch (err) {
      console.error('Error fetching issues:', err);
      setActionItems([]);
    }
  };

  const openReplyModal = (item) => {
    setSelectedIssueItem(item);
    setIssueWorkflowMode('reply');
    setIssueReplyRemark((item?.replyRemark || '').toString());
    setIssueReplyRemarkFieldError('');
    setIssueWorkflowModalOpen(true);
  };

  const openViewModal = (item) => {
    setSelectedIssueItem(item);
    setIssueWorkflowMode('view');
    setIssueReplyRemark((item?.replyRemark || '').toString());
    setIssueReplyRemarkFieldError('');
    setIssueWorkflowModalOpen(true);
  };

  const handleUpdateIssueRemarkOnly = async () => {
    const id = selectedIssueItem?.actionItemID ?? selectedIssueItem?.nCRFActionItemNo;
    if (!id) return;
    if (!issueReplyRemark.trim()) {
      setIssueReplyRemarkFieldError(actionItemMessages.replyRemarkRequired);
      return;
    }
    setIssueReplyRemarkFieldError('');
    setIssueReplyBusyKind('update');
    try {
      const res = await api.post(`/action-items/${id}/update`, {
        replyRemark: issueReplyRemark.trim(),
        submitToCreator: false,
      });
      setToast({ type: 'success', message: actionItemMessages.remarkSavedSuccess });
      await refetchActionItems();
      setIssueWorkflowModalOpen(false);
      setSelectedIssueItem(null);
      setIssueReplyRemark('');
      setIssueReplyRemarkFieldError('');
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to update remark.';
      setToast({ type: 'error', message: msg });
    } finally {
      setIssueReplyBusyKind(null);
    }
  };

  const handleResolveIssue = async () => {
    const id = selectedIssueItem?.actionItemID ?? selectedIssueItem?.nCRFActionItemNo;
    if (!id) return;
    if (!issueReplyRemark.trim()) {
      setIssueReplyRemarkFieldError(actionItemMessages.replyRemarkRequired);
      return;
    }
    setIssueReplyRemarkFieldError('');
    setIssueReplyBusyKind('finalize');
    try {
      await api.post(`/action-items/${id}/update`, {
        replyRemark: issueReplyRemark.trim(),
        submitToCreator: true,
      });
      setToast({ type: 'success', message: actionItemMessages.issueSubmittedToCreatorSuccess });
      setIssueWorkflowModalOpen(false);
      setSelectedIssueItem(null);
      setIssueReplyRemark('');
      setIssueReplyRemarkFieldError('');
      await refetchActionItems();
      dispatchNotificationsRefresh();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to resolve issue.';
      setToast({ type: 'error', message: msg });
    } finally {
      setIssueReplyBusyKind(null);
    }
  };

  const runAfterActionItemReplyESign = async () => {
    setShowActionItemReplyESign(false);
    const intent = issueReplyESignIntentRef.current;
    issueReplyESignIntentRef.current = null;
    if (intent === 'update') {
      await handleUpdateIssueRemarkOnly();
    } else if (intent === 'finalize') {
      await handleResolveIssue();
    }
  };

  const runAfterActionItemCloseESign = async () => {
    setShowActionItemCloseESign(false);
    const id = actionItemIdToClose;
    setActionItemIdToClose(null);
    if (!id) return;
    setActionItemClosingId(id);
    try {
      await api.post(`/action-items/${id}/close`, {});
      await refetchActionItems();
      setIssueWorkflowModalOpen(false);
      setSelectedIssueItem(null);
      setIssueReplyRemark('');
      setIssueReplyRemarkFieldError('');
      setToast({ type: 'success', message: commonMessages.authSuccess });
      dispatchNotificationsRefresh();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to close issue.';
      setToast({ type: 'error', message: msg });
    } finally {
      setActionItemClosingId(null);
    }
  };

  const fetchVisits = async () => {
    const gen = ++visitsFetchGenerationRef.current;
    try {
      setLoading(true);
      const projectCodeForApi = currentProject == null || currentProject === 'all'
        ? undefined
        : (typeof currentProject === 'string' ? currentProject : (currentProject?.projectCode ?? currentProject?.ProjectCode));
      // Always bind visits using stage-wise API to avoid broad fetch.
      const stageForApi = activeStage === 'schedule'
        ? 'Schedule'
        : activeStage === 'onGoing'
          ? 'In Progress'
          : getLabelForStageId(activeStage);
      const response = await visitService.getAll(projectCodeForApi ?? undefined, stageForApi);
      if (gen !== visitsFetchGenerationRef.current) return;
      setVisits(Array.isArray(response) ? response : []);
      setError(null);
    } catch (err) {
      if (gen !== visitsFetchGenerationRef.current) return;
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load visits';
      setError(errorMessage);
      console.error('Error fetching visits:', err);
      setVisits([]);
    } finally {
      if (gen === visitsFetchGenerationRef.current) {
        setLoading(false);
      }
    }
  };

  const refreshVisitsAndStageCounts = () => {
    fetchVisits();
    void refetchStageCounts();
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
    setSelectedDate(null);
  };

  const handleStageChange = (stage) => {
    setActiveStage(stage);
    setSelectedDate(null);
    setReviewTab('pending'); // Reset tab when changing stage
    // Switch to calendar view for schedule stage, panel view for others. SRM: Second Review opens in Panel by default.
    if (stage === 'schedule') {
      setViewMode('calendar');
    } else {
      setViewMode('panel');
    }
  };

  const handleDateClick = (date) => {
    setVisitBeingEdited(null);
    if (activeStage !== 'schedule' || !canSchedule) return;
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const clickedStart = date ? new Date(date) : null;
    if (clickedStart) clickedStart.setHours(0, 0, 0, 0);
    if (clickedStart && clickedStart < todayStart) return;
    setSelectedDate(date);
    setIsScheduleModalOpen(true);
  };

  const handleScheduleModalClose = () => {
    setIsScheduleModalOpen(false);
    setSelectedDate(null);
    setVisitBeingEdited(null);
  };

  const handleScheduleModalSave = () => {
    setToast({ type: 'success', message: visitMessages.savedSuccess });
    refreshVisitsAndStageCounts();
  };

  const handleCalendarEventClick = (visit) => {
    if (!visit) return;
    setSelectedVisitForActions(visit);
    setIsActionsModalOpen(true);
  };

  const handleModifyFromActions = (visit) => {
    if (!visit || activeStage !== 'schedule' || !canSchedule) return;
    setIsActionsModalOpen(false);
    setSelectedVisitForActions(null);
    setVisitBeingEdited(visit);
    setSelectedDate(visit.plannedStartDate ? new Date(visit.plannedStartDate) : null);
    setIsScheduleModalOpen(true);
  };

  const handleOpenConfirmPIModal = (visit) => {
    if (!visit) return;
    setIsActionsModalOpen(false);
    setSelectedVisitForActions(null);
    setVisitForConfirmPI(visit);
    setIsConfirmPIModalOpen(true);
  };

  const handleOpenGenerateLetterModal = (visit) => {
    if (!visit) return;
    setIsActionsModalOpen(false);
    setSelectedVisitForActions(null);
    setVisitForLetter(visit);
    setIsGenerateLetterModalOpen(true);
  };

  const handleOpenGenerateFollowUpLetterModal = (visit) => {
    if (!visit) return;
    setVisitForFollowUpLetter(visit);
    setIsGenerateFollowUpLetterModalOpen(true);
  };

  const handleOpenExecuteVisitModal = (visit) => {
    if (!visit) return;
    setIsActionsModalOpen(false);
    setSelectedVisitForActions(null);
    setVisitForExecute(visit);
    setIsExecuteVisitModalOpen(true);
  };

  const getVisitId = (v) =>
    v?.scheduledVisitId ?? v?.scheduledVisitID ?? v?.ScheduledVisitID ??
    v?.visitNodeId ?? v?.VisitNodeId ?? v?.visitID ?? v?.VisitID ?? v?.visitId;

  const handleStartReport = async (visit) => {
    const vid = getVisitId(visit);
    if (vid == null) return;
    try {
      const data = await visitService.getAssignedCRFTemplate(vid);
      const templateId = data?.templateId ?? data?.TemplateId ?? data?.templateNo ?? data?.TemplateNo;
      if (templateId) {
        setReportTemplateId(templateId);
        setReportVisit(visit);
        setIsReportModalOpen(true);
      } else {
        setToast({ type: 'error', message: 'No Report template assigned for this visit. Please map a template in Report Management (Map Template to Visit).' });
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to open report.';
      setToast({ type: 'error', message: msg || 'No Report template assigned for this visit. Please map a template in Report Management (Map Template to Visit).' });
    }
  };

  const handleDraftSaved = (visitId, nCRFHdrNo) => {
    setToast({ type: 'success', message: commonMessages.authSuccess });
    // Only switch "Start Report" to "Continue" when a CRF header was actually saved (entry exists in CRFHdr)
    if (visitId != null && nCRFHdrNo != null && nCRFHdrNo !== '') setVisitsWithCRFDraft(prev => new Set([...prev, Number(visitId)]));
    // Close the CRF Report modal when user clicks Draft Save (success)
    handleCloseReportModal();
  };

  const handleCloseReportModal = () => {
    setIsReportModalOpen(false);
    setReportTemplateId(null);
    setReportVisit(null);
    // Refresh visit list so CRA submit / stage changes are reflected without manual page refresh
    refreshVisitsAndStageCounts();
  };

  /** Open CRF page for review (First Review / Second Review). Reuses same CRF modal as In Progress. */
  const handleReviewCRFPage = async (visit) => {
    const vid = getVisitId(visit);
    if (vid == null) return;
    try {
      const data = await visitService.getAssignedCRFTemplate(vid);
      const templateId = data?.templateId ?? data?.TemplateId ?? data?.templateID ?? data?.templateNo ?? data?.TemplateNo ?? data?.vTemplateNo ?? data?.VTemplateNo;
      if (templateId) {
        setReportTemplateId(templateId);
        setReportVisit(visit);
        setIsReportModalOpen(true);
      } else {
        setToast({ type: 'error', message: 'No Report template assigned for this visit.' });
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to open CRF for review.';
      setToast({ type: 'error', message: msg });
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case VISIT_STATUS.COMPLETED:
        return 'badge-success';
      case VISIT_STATUS.ONGOING:
        return 'badge-info';
      case VISIT_STATUS.CONFIRMED:
        return 'badge-warning';
      case VISIT_STATUS.CANCELLED:
        return 'badge-error';
      default:
        return 'badge-default';
    }
  };

  // Filter visits by stage
  const getVisitsByStage = (stage) => {
    if (!Array.isArray(visits) || visits.length === 0) {
      return [];
    }

    // Same site/cra filter used for visit list and for action-item visit pool (align with Visit visibility)
    const filtered = visits.filter(v => {
      if (!v) return false;
      if (filters.site !== 'all' && (v.siteCode ?? v.SiteCode) !== filters.site) return false;
      if (filters.cra !== 'all' && getVisitCraFilterId(v) !== filters.cra) return false;
      return true;
    });

    const getStatus = (v) => v.visitStatus ?? v.VisitStatus;
    const getCrfStage = (v) => v.crfStage ?? v.CrfStage ?? '';

    const completedLabel = getLabelForStageId('closed');
    const inProgressLabel = getLabelForStageId('onGoing');

    switch (stage) {
      case 'schedule':
        return filtered.filter(v => {
          const s = getStatus(v);
          return s === VISIT_STATUS.DRAFT || s === VISIT_STATUS.PLANNED || s === VISIT_STATUS.CONFIRMED;
        });
      case 'onGoing': {
        // In Progress tab: visits whose CrfStage is "In Progress" or unset, OR visits with open DCF (CRA must fix) even when CrfStage is First/Second Review.
        return filtered.filter(v => {
          const crf = getCrfStage(v);
          const hasOpenDCF = v.hasOpenDCF ?? v.HasOpenDCF;
          if (hasOpenDCF) return true;
          if (crf !== '' && crf !== inProgressLabel) return false;
          return getStatus(v) === VISIT_STATUS.ONGOING || (inProgressLabel && crf === inProgressLabel);
        });
      }
      case 'closed':
        return filtered.filter(v => {
          const crf = getCrfStage(v);
          return getStatus(v) === VISIT_STATUS.COMPLETED || (completedLabel && crf === completedLabel);
        });
      default: {
        // Dynamic review stage (id = stageCode string e.g. '301', '311')
        const stageLabel = getLabelForStageId(stage);
        if (!stageLabel) return [];
        return filtered.filter(v => {
          const crf = getCrfStage(v);
          if (completedLabel && crf === completedLabel) return false;
          const hasOpenDCF = v.hasOpenDCF ?? v.HasOpenDCF;
          const openDCFStageRaised = v.openDCFStageRaised ?? v.OpenDCFStageRaised;
          if (hasOpenDCF && openDCFStageRaised === stageLabel) return true;
          return crf === stageLabel;
        });
      }
    }
  };

  const getStageCount = (stage) => {
    return getVisitsByStage(stage).length;
  };

  // Dynamic stages: only 101, 201, 9999 are fixed; between 201 and 9999 use stageCode as id (e.g. 301, 311, 321).
  const stageCodeToId = (code) => {
    if (code === STAGE_WORKFLOW.SCHEDULE) return 'schedule';
    if (code === STAGE_WORKFLOW.IN_PROGRESS) return 'onGoing';
    if (code === STAGE_WORKFLOW.COMPLETED) return 'closed';
    if (code > STAGE_WORKFLOW.IN_PROGRESS && code < STAGE_WORKFLOW.COMPLETED) return String(code);
    return null;
  };

  const getLabelForStageId = (stageId) => {
    const code = stageId === 'schedule' ? STAGE_WORKFLOW.SCHEDULE : stageId === 'onGoing' ? STAGE_WORKFLOW.IN_PROGRESS : stageId === 'closed' ? STAGE_WORKFLOW.COMPLETED : parseInt(stageId, 10);
    if (Number.isNaN(code)) return '';
    const s = workflowStagesFromApi.find((x) => (x.stageCode ?? x.StageCode) === code);
    return s ? (s.label ?? s.Label ?? s.vDisplayName ?? s.VDisplayName ?? '') : '';
  };

  const isStageWithVisitsAndActions = activeStage === 'onGoing' || (activeStage !== 'schedule' && activeStage !== 'closed');
  const showVisitsTable = (activeStage === 'schedule' || activeStage === 'closed') ||
    (activeStage === 'onGoing' && reviewTab === 'pending') ||
    (isStageWithVisitsAndActions && reviewTab === 'pending');

  const visitsTableFilteredCount = (() => {
    if (!showVisitsTable) return 0;
    const stageVisits = getVisitsByStage(activeStage);
    return stageVisits.filter((v) => visitRowMatchesSearch(v, visitSearch)).length;
  })();

  const showActionItemsTable = reviewTab === 'actions' && (activeStage === 'onGoing' || (activeStage !== 'schedule' && activeStage !== 'closed'));

  const actionItemsTableData = (() => {
    if (!showActionItemsTable) {
      return { paginatedList: [], totalCount: 0, visitById: new Map(), unfilteredIssueCount: 0 };
    }
    const baseFiltered = visits.filter((v) => {
      if (!v) return false;
      if (filters.site !== 'all' && (v.siteCode ?? v.SiteCode) !== filters.site) return false;
      if (filters.cra !== 'all' && getVisitCraFilterId(v) !== filters.cra) return false;
      return true;
    });
    const visitById = new Map();
    baseFiltered.forEach((v) => {
      const id = getVisitId(v);
      if (id != null) {
        const n = Number(id);
        visitById.set(n, v);
        if (String(id) !== String(n)) visitById.set(id, v);
      }
    });
    const getItemVisitId = (item) => item.scheduledVisitID ?? item.ScheduledVisitID;
    const enrichedList = actionItems.map((item) => {
      const vid = getItemVisitId(item);
      const visit = vid != null ? (visitById.get(Number(vid)) ?? visitById.get(vid)) : null;
      return { ...item, visit: visit || null };
    });
    const searchedList = enrichedList.filter((row) =>
      actionItemRowMatchesSearch(row, row.visit, actionItemSearch),
    );
    const totalCount = searchedList.length;
    const unfilteredIssueCount = enrichedList.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / actionItemPageSize));
    const currentPage = Math.min(actionItemPage, totalPages);
    const start = (currentPage - 1) * actionItemPageSize;
    const paginatedList = searchedList.slice(start, start + actionItemPageSize);
    return { paginatedList, totalCount, visitById, unfilteredIssueCount };
  })();

  const actionItemPagerTotalPages = Math.max(
    1,
    Math.ceil(actionItemsTableData.totalCount / actionItemPageSize),
  );
  const actionItemPagerPage = Math.min(actionItemPage, actionItemPagerTotalPages);

  const workflowStages = (() => {
    // Show only stages returned by API (profile-filtered by backend). No fallback to all stages.
    if (!stagesLoaded) return []; // Before first load, show no tabs
    if (workflowStagesFromApi.length === 0) return []; // No stages assigned to profile
    return workflowStagesFromApi
      .filter((s) => (s.stageCode ?? s.StageCode) != null)
      .sort((a, b) => (a.orderNo ?? a.OrderNo ?? 0) - (b.orderNo ?? b.OrderNo ?? 0))
      .map((s) => {
        const code = s.stageCode ?? s.StageCode;
        const id = stageCodeToId(code);
        return {
          id,
          stageCode: code,
          label: s.label ?? s.Label ?? s.vDisplayName ?? s.VDisplayName ?? id,
          count: stageCountsByCode[Number(code)] ?? getStageCount(id),
        };
      });
  })();

  // When stages load: apply ?stage= / ?tab=issues from dashboard (or other deep links), then clamp activeStage to allowed ids
  useEffect(() => {
    if (!stagesLoaded || workflowStagesFromApi.length === 0) return;
    const sorted = [...workflowStagesFromApi]
      .filter((s) => (s.stageCode ?? s.StageCode) != null)
      .sort((a, b) => (a.orderNo ?? a.OrderNo ?? 0) - (b.orderNo ?? b.OrderNo ?? 0));
    const ids = sorted.map((s) => {
      const code = s.stageCode ?? s.StageCode;
      if (code === STAGE_WORKFLOW.SCHEDULE) return 'schedule';
      if (code === STAGE_WORKFLOW.IN_PROGRESS) return 'onGoing';
      if (code === STAGE_WORKFLOW.COMPLETED) return 'closed';
      if (code > STAGE_WORKFLOW.IN_PROGRESS && code < STAGE_WORKFLOW.COMPLETED) return String(code);
      return null;
    }).filter(Boolean);

    const stageParamRaw = searchParams.get('stage');
    const wantsIssues = searchParams.get('tab') === 'issues';
    const hasNavIntent = (stageParamRaw != null && String(stageParamRaw).trim() !== '') || wantsIssues;

    if (hasNavIntent) {
      let target = parseVisitsUrlStageParam(stageParamRaw);
      if (target && !ids.includes(target)) target = null;
      if (wantsIssues) {
        setReviewTab('actions');
        if (!target) {
          if (ids.includes('onGoing')) target = 'onGoing';
          else target = ids.find((id) => id !== 'schedule' && id !== 'closed') ?? ids[0];
        }
      }
      if (target && ids.includes(target)) {
        setActiveStage(target);
        setViewMode(target === 'schedule' ? 'calendar' : 'panel');
        const profileCode = (user?.profileCode ?? user?.vUserTypeCode ?? user?.role ?? '').toString().trim().toUpperCase();
        const targetRow = sorted.find((s) => stageCodeToActiveStageId(s.stageCode ?? s.StageCode) === target);
        const targetLabel = (targetRow?.label ?? targetRow?.Label ?? targetRow?.vDisplayName ?? targetRow?.VDisplayName ?? '').toString().toLowerCase();
        if (profileCode === 'SRM' && targetLabel.includes('second review')) setViewMode('panel');
      }
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('stage');
        next.delete('tab');
        return next;
      }, { replace: true });
      if (target && ids.includes(target)) return;
    }

    if (ids.length > 0 && !ids.includes(activeStage)) {
      setActiveStage(ids[0]);
      // SRM profile: when default stage is Second Review, open Panel View instead of Calendar
      const firstStageLabel = (sorted[0]?.label ?? sorted[0]?.Label ?? sorted[0]?.vDisplayName ?? sorted[0]?.VDisplayName ?? '').toString().toLowerCase();
      const profileCode = (user?.profileCode ?? user?.vUserTypeCode ?? user?.role ?? '').toString().trim().toUpperCase();
      if (profileCode === 'SRM' && firstStageLabel.includes('second review')) setViewMode('panel');
    }
  }, [stagesLoaded, workflowStagesFromApi, searchParams, activeStage, user, setSearchParams]);

  // Get action items for selected visit (CRFActionItems from GET /api/action-items?stage=... include scheduledVisitID)
  const getVisitActionItems = (visitId) => {
    if (visitId == null) return [];
    const id = Number(visitId);
    return actionItems.filter(
      (item) => Number(item.scheduledVisitID) === id || item.scheduledVisitID == visitId
    );
  };

  // Render stage-specific table columns
  const renderTableHeaders = () => {
    switch (activeStage) {
      case 'schedule':
        return (
          <tr>
            <th>Visit ID</th>
            <th>Site</th>
            <th>Visit Type</th>
            <th>Scheduled Start Date</th>
            <th>Scheduled End Date</th>
            <th>Edit</th>
            <th style={{ textAlign: 'center' }}>Actions</th>
          </tr>
        );
      case 'onGoing':
        if (reviewTab === 'actions') {
          return (
            <tr>
              <th>Reference No</th>
              <th>Visit ID</th>
              <th>Site</th>
              <th>Visit Type</th>
              <th>Issue On</th>
              <th>Issue description</th>
              <th>Assigned To</th>
              <th>Due Date</th>
              <th>Priority</th>
              <th>Current Status</th>
              <th>Details</th>
              <th>Actions</th>
            </tr>
          );
        }
        return (
          <tr>
            <th>Visit ID</th>
            <th>Site</th>
            <th>Visit Type</th>
            <th>Visit Start Date</th>
            <th>Visit End Date</th>
            <th style={{ textAlign: 'center' }}>Actions</th>
          </tr>
        );
      case 'closed':
        return (
          <tr>
            <th>Visit ID</th>
            <th>Site</th>
            <th>Visit Type</th>
            <th>Start Date</th>
            <th>End Date</th>
            <th>Status</th>
            <th style={{ textAlign: 'center' }}>Actions</th>
          </tr>
        );
      default:
        if (isStageWithVisitsAndActions) {
          if (reviewTab === 'pending') {
            return (
              <tr>
                <th>Visit ID</th>
                <th>Site</th>
                <th>Visit Type</th>
                <th>Scheduled Start Date</th>
                <th>Scheduled End Date</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            );
          }
          return (
            <tr>
              <th>Reference No</th>
              <th>Visit ID</th>
              <th>Site</th>
              <th>Visit Type</th>
              <th>Issue On</th>
              <th>Issue description</th>
              <th>Assigned To</th>
              <th>Due Date</th>
              <th>Priority</th>
              <th>Current Status</th>
              <th>Details</th>
              <th>Actions</th>
            </tr>
          );
        }
        return null;
    }
  };

  // Render stage-specific table rows
  const renderTableRows = () => {
    const stageVisits = getVisitsByStage(activeStage);
    /** Schedule + Completed have 7 columns; other visit grids (e.g. In Progress) have 6. Must match thead or borders/alignment break. */
    const visitsEmptyRowColSpan = activeStage === 'schedule' || activeStage === 'closed' ? 7 : 6;

    const renderActionItemsGrid = (itemsForStage, visitById) => {
      const getItemVisitId = (item) => item.scheduledVisitID ?? item.ScheduledVisitID;
      const visitActionItems = itemsForStage.map((item) => {
        const vid = getItemVisitId(item);
        const visit = vid != null ? (visitById.get(Number(vid)) ?? visitById.get(vid)) : null;
        return { ...item, visit: visit || null };
      });
      if (visitActionItems.length === 0) {
        const q = (actionItemSearch || '').trim();
        const hadRows = actionItemsTableData.unfilteredIssueCount > 0;
        const emptyMsg = q && hadRows ? 'No issues match your search.' : 'No issues found';
        return (
          <tr>
            <td colSpan="12" style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
              {emptyMsg}
            </td>
          </tr>
        );
      }
      return visitActionItems.map((item) => (
          <tr key={item.actionItemID ?? item.nCRFActionItemNo ?? item.actionItemNumber}>
            <td>{item.actionItemNumber ?? item.vActionItemNumber ?? '-'}</td>
            <td>{getActionItemVisitIdDisplay(item, item.visit)}</td>
            <td>{getActionItemSiteDisplay(item, item.visit, sites)}</td>
            <td>{getActionItemVisitTypeLabel(item, item.visit)}</td>
            <td>{item.actionItemOn || '-'}</td>
            <td>{item.findingDescription || '-'}</td>
            <td>{item.assignedToName || 'CRA'}</td>
            <td>{item.dueDate ? formatDateDDMMMYYYY(item.dueDate) : '-'}</td>
            <td>{item.priorityDescription ?? item.severity ?? '—'}</td>
            <td>
              <span
                className={`badge ${
                  item.status === 'Open'
                    ? 'badge-warning'
                    : item.status === 'Resolved'
                      ? 'badge-info'
                      : 'badge-success'
                }`}
              >
                {item.status || 'Open'}
              </span>
            </td>
            <td style={{ textAlign: 'center' }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedIssueForDetails(item);
                  setIssueDetailsModalOpen(true);
                }}
              >
                Details
              </button>
            </td>
            <td style={{ textAlign: 'center' }}>
              {(() => {
                const status = (item.status || '').toLowerCase();
                const currentProfile = (user?.profileCode ?? user?.vUserTypeCode ?? user?.role ?? '').toString().trim().toUpperCase();
                const assignedProfile = (item.assignedToProfileCode ?? '').toString().trim().toUpperCase();
                const canReply = status === 'open' && currentProfile && assignedProfile && currentProfile === assignedProfile;
                const sessionMid = Number(sessionRecordedByMappingId ?? 0) || 0;
                const canViewForClose = status === 'resolved' && sessionMid > 0 && Number(item.createdBy || 0) === sessionMid;
                if (canReply) {
                  return (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        openReplyModal(item);
                      }}
                    >
                      Reply
                    </button>
                  );
                }
                if (canViewForClose) {
                  return (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        openViewModal(item);
                      }}
                    >
                      View
                    </button>
                  );
                }
                return <span className="text-muted">-</span>;
              })()}
            </td>
          </tr>
        ));
    };

    if (activeStage === 'onGoing' && reviewTab === 'actions') {
      return renderActionItemsGrid(actionItemsTableData.paginatedList, actionItemsTableData.visitById);
    }

    if (isStageWithVisitsAndActions && activeStage !== 'onGoing' && reviewTab === 'actions') {
      return renderActionItemsGrid(actionItemsTableData.paginatedList, actionItemsTableData.visitById);
    }

    if (stageVisits.length === 0) {
      return (
        <tr className="visit-table-empty-row">
          <td colSpan={visitsEmptyRowColSpan} className="visit-table-empty-cell">
            No visits found in this stage
          </td>
        </tr>
      );
    }

    const q = (visitSearch || '').trim().toLowerCase();
    const filteredVisits = stageVisits.filter((v) => visitRowMatchesSearch(v, visitSearch));
    // Sort by Visit ID (numeric) ascending for grid display
    const sortedVisits = [...filteredVisits].sort((a, b) => {
      const idA = Number(getVisitId(a)) || 0;
      const idB = Number(getVisitId(b)) || 0;
      return idA - idB;
    });
    const totalItems = sortedVisits.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / visitPageSize));
    const currentPage = Math.min(visitPage, totalPages);
    const start = (currentPage - 1) * visitPageSize;
    const paginatedVisits = sortedVisits.slice(start, start + visitPageSize);

    if (paginatedVisits.length === 0) {
      return (
        <tr className="visit-table-empty-row">
          <td colSpan={visitsEmptyRowColSpan} className="visit-table-empty-cell">
            {q ? 'No visits match your search.' : 'No visits found in this stage'}
          </td>
        </tr>
      );
    }

    return paginatedVisits.map((visit) => {
      if (!visit) return null;

      const vid = getVisitId(visit);
      const visitId = formatVisitId(visit);

      switch (activeStage) {
        case 'schedule':
          return (
            <tr
              key={getVisitId(visit)}
            >
              <td>{visitId}</td>
              <td>{visit.vSiteCode ?? visit.siteCode ?? visit.SiteCode ?? '—'}</td>
              <td>{VISIT_TYPES[visit.visitType] || visit.visitType || '—'}</td>
              <td>{visit.plannedStartDate ? formatDateDDMMMYYYY(visit.plannedStartDate) : '—'}</td>
              <td>{visit.plannedEndDate ? formatDateDDMMMYYYY(visit.plannedEndDate) : '—'}</td>
              <td>
                <div className="action-buttons-inline">
                  {canSchedule && (
                    <button
                      type="button"
                      className="action-btn"
                      title="Edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        setVisitBeingEdited(visit);
                        setSelectedDate(visit.plannedStartDate ? new Date(visit.plannedStartDate) : null);
                        setIsScheduleModalOpen(true);
                      }}
                    >
                      <FiEdit size={18} />
                    </button>
                  )}
                  
                </div>
              </td>
              <td style={{ textAlign: 'center' }}>
                <div className="stage-actions">
                  {canVisitActivity && (
                    <>
                      {(visit.confirmationStatus || visit.ConfirmationStatus) !== 'Confirmed' && !(visit.hasVisitLetter || visit.HasVisitLetter) && (
                        <button className="btn btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); setVisitForLetter(visit); setIsGenerateLetterModalOpen(true); }}>
                          Generate Visit Letter
                        </button>
                      )}
                      {(visit.confirmationStatus || visit.ConfirmationStatus) !== 'Confirmed' && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            const hasLetter = visit.hasVisitLetter || visit.HasVisitLetter;
                            if (!hasLetter) {
                              setToast({ type: 'info', message: 'Please generate the visit letter first before confirming PI availability.' });
                              return;
                            }
                            setVisitForConfirmPI(visit);
                            setIsConfirmPIModalOpen(true);
                          }}
                        >
                          Confirm PI Availability
                        </button>
                      )}
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          const hasLetter = visit.hasVisitLetter || visit.HasVisitLetter;
                          const isConfirmed = (visit.confirmationStatus || visit.ConfirmationStatus) === 'Confirmed';
                          if (!hasLetter) {
                            setToast({ type: 'info', message: 'Please generate the visit letter first before executing the visit.' });
                            return;
                          }
                          if (!isConfirmed) {
                            setToast({ type: 'info', message: 'Please confirm PI availability first before executing the visit.' });
                            return;
                          }
                          setVisitForExecute(visit);
                          setIsExecuteVisitModalOpen(true);
                        }}
                      >
                        Execute Visit
                      </button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          );
        case 'onGoing':
          return (
            <tr
              key={getVisitId(visit)}
            >
              <td>{visitId}</td>
              <td>{visit.vSiteCode ?? visit.siteCode ?? visit.SiteCode ?? '—'}</td>
              <td>{VISIT_TYPES[visit.visitType] || visit.visitType || '—'}</td>
              <td>{visit.actualStartDate ? formatDateDDMMMYYYY(visit.actualStartDate) : (visit.plannedStartDate ? formatDateDDMMMYYYY(visit.plannedStartDate) : '—')}</td>
              <td>{visit.actualEndDate ? formatDateDDMMMYYYY(visit.actualEndDate) : (visit.plannedEndDate ? formatDateDDMMMYYYY(visit.plannedEndDate) : '—')}</td>
              <td style={{ textAlign: 'center' }}>
                {(canStartReport || isViewOnly) && (
                  <button className="btn btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); handleStartReport(visit); }}>
                    {isViewOnly ? 'View' : (visitsWithCRFDraft.has(Number(getVisitId(visit))) ? 'Draft Report' : 'Start Report')}
                  </button>
                )}
              </td>
            </tr>
          );
        case 'closed':
          return (
            <tr
              key={getVisitId(visit)}
            >
              <td>{visitId}</td>
              <td>{visit.vSiteCode ?? visit.siteCode ?? visit.SiteCode ?? '—'}</td>
              <td>{VISIT_TYPES[visit.visitType] || visit.visitType || '—'}</td>
              <td>{visit.actualStartDate ? formatDateDDMMMYYYY(visit.actualStartDate) : (visit.plannedStartDate ? formatDateDDMMMYYYY(visit.plannedStartDate) : '—')}</td>
              <td>{visit.actualEndDate ? formatDateDDMMMYYYY(visit.actualEndDate) : (visit.plannedEndDate ? formatDateDDMMMYYYY(visit.plannedEndDate) : '—')}</td>
              <td>
                <span className={`badge ${getStatusBadgeClass(visit.visitStatus)}`}>
                  {(visit.isFullyCompleted ?? visit.IsFullyCompleted) ? VISIT_STATUS.COMPLETED : (visit.visitStatus || '—')}
                </span>
              </td>
              <td style={{ textAlign: 'center' }}>
                {canVisitActivity && (
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                    {(visit.hasFollowUpLetter ?? visit.HasFollowUpLetter) ? (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenGenerateFollowUpLetterModal(visit);
                        }}
                      >
                        View
                      </button>
                    ) : !(visit.isFullyCompleted ?? visit.IsFullyCompleted) ? (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenGenerateFollowUpLetterModal(visit);
                        }}
                      >
                        Follow-Up Letter
                      </button>
                    ) : null}

                    {(visit.hasFollowUpLetter ?? visit.HasFollowUpLetter)
                      && !(visit.isFullyCompleted ?? visit.IsFullyCompleted)
                      && !String(visit.followUpConfirmationStatus ?? visit.FollowUpConfirmationStatus ?? '').trim().toLowerCase().includes('confirmed')
                      && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setVisitForFollowUpConfirmPI(visit);
                            setIsConfirmFollowUpPIModalOpen(true);
                          }}
                        >
                          Confirm PI Availability
                        </button>
                      )}
                  </div>
                )}
              </td>
            </tr>
          );
        default:
          if (isStageWithVisitsAndActions && activeStage !== 'onGoing' && reviewTab === 'pending') {
            return (
              <tr key={getVisitId(visit)}>
                <td>{visitId}</td>
                <td>{visit.vSiteCode ?? visit.siteCode ?? visit.SiteCode ?? '—'}</td>
                <td>{VISIT_TYPES[visit.visitType] || visit.visitType || '—'}</td>
                <td>{visit.plannedStartDate ? formatDateDDMMMYYYY(visit.plannedStartDate) : '—'}</td>
                <td>{visit.plannedEndDate ? formatDateDDMMMYYYY(visit.plannedEndDate) : '—'}</td>
                <td style={{ textAlign: 'center' }}>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReviewCRFPage(visit);
                    }}
                  >
                    Review CRF
                  </button>
                </td>
              </tr>
            );
          }
          return null;
      }
    });
  };

  if (loading) return <Spinner />;

  return (
    <div className="visit-management">
      {error && <Alert type="error" message={error} />}

      {/* Filters */}
      <div className="visit-filters">
        <div className="filter-group">
          <label className="form-label">Filter by Site</label>
          <select
            className="form-select"
            value={filters.site}
            onChange={(e) => handleFilterChange('site', e.target.value)}
            disabled={loadingFilters}
          >
            <option value="all">All Sites</option>
            {loadingFilters ? (
              <option value="">Loading sites...</option>
            ) : (
              sites.map(site => (
                <option key={site.siteID} value={site.siteCode}>{site.siteDisplay || site.siteCode}</option>
              ))
            )}
          </select>
        </div>
        <div className="filter-group">
          <label className="form-label">Filter by CRA</label>
          <select
            className="form-select"
            value={filters.cra}
            onChange={(e) => handleFilterChange('cra', e.target.value)}
            disabled={loadingFilters}
          >
            <option value="all">All CRA</option>
            {loadingFilters ? (
              <option value="">Loading CRA...</option>
            ) : cras.length === 0 ? (
              <option value="">No CRA available</option>
            ) : (
              cras.map(cra => (
                <option key={cra.id} value={cra.id}>{cra.name}</option>
              ))
            )}
          </select>
        </div>
      </div>

      {/* Workflow Steps – only stages the user has rights to (profile-filtered from API) */}
      {stagesLoaded && workflowStages.length === 0 ? (
        <div className="visit-no-stages" style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
          <p>No stages assigned to your profile. Contact your administrator to get access to stages.</p>
        </div>
      ) : !stagesLoaded ? (
        <div className="workflow-steps" style={{ padding: '1rem', textAlign: 'center', color: '#888' }}>
          Loading stages…
        </div>
      ) : (
        <>
      <div className="workflow-steps">
        {workflowStages.map((stage) => (
          <div
            key={stage.id}
            className={`step ${activeStage === stage.id ? 'active' : ''}`}
            onClick={() => handleStageChange(stage.id)}
          >
            <button className="step-circle">{stage.count}</button>
            <div className="step-label">{stage.label}</div>
          </div>
        ))}
      </div>

      {/* View Toggle Buttons */}
      <div className="view-toggle">
        <button
          className={`btn ${viewMode === 'calendar' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setViewMode('calendar')}
        >
          <FiCalendar /> Calendar View
        </button>
        <button
          className={`btn ${viewMode === 'panel' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setViewMode('panel')}
        >
          <FiColumns /> Panel View
        </button>
      </div>

      {/* Main Content Area */}
      <div className="visit-content-area">
        {viewMode === 'panel' ? (
          <div className="visit-panel-view">
            {/* Tabs for In Progress, First Review and Second Review */}
            {isStageWithVisitsAndActions && (
              <div className="review-tabs">
                <div
                  className={`review-tab ${reviewTab === 'pending' ? 'active' : ''}`}
                  onClick={() => setReviewTab('pending')}
                >
                  {activeStage === 'onGoing' ? 'In Progress' : (getLabelForStageId(activeStage) || 'Pending Reviews')}
                </div>
                <div
                  className={`review-tab ${reviewTab === 'actions' ? 'active' : ''}`}
                  onClick={() => {
                    setReviewTab('actions');
                    refetchActionItems();
                  }}
                  title="Issues"
                >
                  <FiList size={18} aria-hidden /> Issues
                </div>
              </div>
            )}

            {/* Action Items Header for In Progress / Review Stages */}
            {reviewTab === 'actions' && isStageWithVisitsAndActions && (
              <div className="review-actions-header">
                <h4>Open Issues</h4>
                {!isViewOnly && (
                  <button
                    type="button"
                    className="btn btn-primary btn-icon-only"
                    onClick={() => setIsCreateActionItemModalOpen(true)}
                    title="Create Issue"
                    aria-label="Create Issue"
                  >
                    <FiPlus size={20} aria-hidden /> Create Issue
                  </button>
                )}
              </div>
            )}

            <div className="visit-table-container" style={{ position: 'relative' }}>
              {activeStage === 'onGoing' && reviewTab === 'pending' && loadingCRFDraftStatus && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255,255,255,0.7)',
                    zIndex: 10,
                  }}
                  aria-live="polite"
                  aria-busy="true"
                >
                  <Spinner />
                </div>
              )}
              {showVisitsTable && (
                <div className="visit-table-toolbar data-grid-toolbar">
                  <div className="data-grid-controls">
                    <div className="data-grid-search">
                      <input
                        type="text"
                        className="form-input data-grid-search-input"
                        placeholder="Search"
                        value={visitSearch}
                        onChange={(e) => {
                          setVisitSearch(e.target.value);
                          setVisitPage(1);
                        }}
                      />
                    </div>
                    <select
                      value={visitPageSize}
                      onChange={(e) => {
                        setVisitPageSize(Number(e.target.value));
                        setVisitPage(1);
                      }}
                      className="form-select page-size-select data-grid-page-size"
                    >
                    <option value={5}>5 items</option>
                    <option value={10}>10 items</option>
                    <option value={25}>25 items</option>
                    <option value={50}>50 items</option>
                  </select>
                  </div>
                </div>
              )}
              {showActionItemsTable && (
                <div className="visit-table-toolbar data-grid-toolbar">
                  <div className="data-grid-controls">
                    <div className="data-grid-search">
                      <input
                        type="search"
                        className="form-input data-grid-search-input"
                        placeholder="Search"
                        value={actionItemSearch}
                        onChange={(e) => {
                          setActionItemSearch(e.target.value);
                          setActionItemPage(1);
                        }}
                        aria-label="Search issues"
                      />
                    </div>
                    <select
                      value={actionItemPageSize}
                      onChange={(e) => {
                        setActionItemPageSize(Number(e.target.value));
                        setActionItemPage(1);
                      }}
                      className="form-select page-size-select data-grid-page-size"
                    >
                      <option value={5}>5 items</option>
                      <option value={10}>10 items</option>
                      <option value={25}>25 items</option>
                      <option value={50}>50 items</option>
                    </select>
                  </div>
                </div>
              )}
              <table className="visit-table">
                <thead>
                  {renderTableHeaders()}
                </thead>
                <tbody>
                  {renderTableRows()}
                </tbody>
              </table>
              {showVisitsTable && visitsTableFilteredCount > 0 && (
                <div className="visit-table-pagination" style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
                  <Pagination
                    page={visitPage}
                    pageSize={visitPageSize}
                    totalItems={visitsTableFilteredCount}
                    onPageChange={setVisitPage}
                    onPageSizeChange={(size) => {
                      setVisitPageSize(size);
                      setVisitPage(1);
                    }}
                    showPageSize={false}
                  />
                </div>
              )}
              {showActionItemsTable && actionItemsTableData.totalCount > 0 && (
                <div className="visit-table-pagination" style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
                  <Pagination
                    page={actionItemPagerPage}
                    pageSize={actionItemPageSize}
                    totalItems={actionItemsTableData.totalCount}
                    onPageChange={setActionItemPage}
                    onPageSizeChange={(size) => {
                      setActionItemPageSize(size);
                      setActionItemPage(1);
                    }}
                    showPageSize={false}
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="visit-calendar-view">
            <VisitCalendar
              visits={getVisitsByStage(activeStage)}
              activeStage={activeStage}
              filters={filters}
              onEventClick={handleCalendarEventClick}
              onDateClick={handleDateClick}
            />
          </div>
        )}
      </div>

      </>
      )}

      {/* Schedule Visit Modal */}
      <ScheduleVisitModal
        isOpen={isScheduleModalOpen}
        onClose={handleScheduleModalClose}
        selectedDate={selectedDate}
        onSave={handleScheduleModalSave}
        defaultSite={filters.site !== 'all' ? filters.site : null}
        mode={visitBeingEdited ? 'edit' : 'create'}
        existingVisit={visitBeingEdited}
      />

      {/* Report Review Modal */}
      <ReportReviewModal
        isOpen={isReviewModalOpen}
        onClose={() => {
          setIsReviewModalOpen(false);
          setSelectedVisitForReview(null);
        }}
        visit={selectedVisitForReview}
        resolvedPiName={getVisitPI(selectedVisitForReview, sites)}
        reportId={selectedVisitForReview?.reportID || null}
      />

      {/* Visit Actions Modal (Calendar event click) */}
      <VisitActionsModal
        isOpen={isActionsModalOpen}
        onClose={() => {
          setIsActionsModalOpen(false);
          setSelectedVisitForActions(null);
        }}
        visit={selectedVisitForActions}
        resolvedPiName={getVisitPI(selectedVisitForActions, sites)}
        onRefresh={refreshVisitsAndStageCounts}
        onModify={handleModifyFromActions}
        onOpenConfirmPI={handleOpenConfirmPIModal}
        onOpenGenerateLetter={handleOpenGenerateLetterModal}
        onOpenExecuteVisit={handleOpenExecuteVisitModal}
        onStartReport={handleStartReport}
        canSchedule={canSchedule}
        canVisitActivity={canVisitActivity}
        canStartReport={canStartReport || isViewOnly}
        isViewOnly={isViewOnly}
        activeStage={activeStage}
      />

      {/* Confirm PI Availability Modal */}
      <ConfirmPIModal
        isOpen={isConfirmPIModalOpen}
        onClose={() => {
          setIsConfirmPIModalOpen(false);
          setVisitForConfirmPI(null);
        }}
        visit={visitForConfirmPI}
        resolvedPiName={getVisitPI(visitForConfirmPI, sites)}
        onConfirmed={() => {
          setToast({ type: 'success', message: commonMessages.authSuccess });
          refreshVisitsAndStageCounts();
        }}
      />

      {/* Generate Visit Letter Modal */}
      <GenerateVisitLetterModal
        isOpen={isGenerateLetterModalOpen}
        onClose={() => {
          setIsGenerateLetterModalOpen(false);
          setVisitForLetter(null);
        }}
        visit={visitForLetter}
        resolvedPiName={getVisitPI(visitForLetter, sites)}
        onGenerated={() => {
          setToast({ type: 'success', message: commonMessages.authSuccess });
          refreshVisitsAndStageCounts();
        }}
      />

      {/* Generate Follow-Up Letter Modal (Completed stage) */}
      <GenerateFollowUpLetterModal
        isOpen={isGenerateFollowUpLetterModalOpen}
        onClose={() => {
          setIsGenerateFollowUpLetterModalOpen(false);
          setVisitForFollowUpLetter(null);
        }}
        visit={visitForFollowUpLetter}
        resolvedPiName={getVisitPI(visitForFollowUpLetter, sites)}
        mode={(() => {
          const v = visitForFollowUpLetter;
          if (!v) return 'edit';
          const fu = String(v.followUpConfirmationStatus ?? v.FollowUpConfirmationStatus ?? '').trim().toLowerCase();
          if (fu === 'rejected') return 'edit';
          return (v.hasFollowUpLetter ?? v.HasFollowUpLetter) ? 'view' : 'edit';
        })()}
        onGenerated={() => {
          setToast({ type: 'success', message: commonMessages.authSuccess });
          refreshVisitsAndStageCounts();
        }}
      />

      {/* Confirm PI Availability (Follow-Up Letter) */}
      <ConfirmFollowUpPIModal
        isOpen={isConfirmFollowUpPIModalOpen}
        onClose={() => {
          setIsConfirmFollowUpPIModalOpen(false);
          setVisitForFollowUpConfirmPI(null);
        }}
        visit={visitForFollowUpConfirmPI}
        resolvedPiName={getVisitPI(visitForFollowUpConfirmPI, sites)}
        onConfirmed={() => {
          setToast({ type: 'success', message: commonMessages.authSuccess });
          refreshVisitsAndStageCounts();
        }}
      />

      {/* Execute Visit Modal */}
      <ExecuteVisitModal
        isOpen={isExecuteVisitModalOpen}
        onClose={() => {
          setIsExecuteVisitModalOpen(false);
          setVisitForExecute(null);
        }}
        visit={visitForExecute}
        resolvedPiName={getVisitPI(visitForExecute, sites)}
        cras={cras}
        onExecuted={() => {
          setToast({ type: 'success', message: commonMessages.authSuccess });
          refreshVisitsAndStageCounts();
        }}
      />

      {/* Create Action Item Modal (Open Action Items tab) */}
      <CreateActionItemModal
        isOpen={isCreateActionItemModalOpen}
        onClose={() => setIsCreateActionItemModalOpen(false)}
        onSave={() => refetchActionItems()}
        defaultProject={currentProject || undefined}
        defaultSiteId=""
        stageVisits={isStageWithVisitsAndActions ? getVisitsByStage(activeStage) : []}
        visitIdsWithCRFDraft={Array.from(visitsWithCRFDraft)}
        pi="Dr. Tapan Shah"
      />

      <Modal
        isOpen={issueDetailsModalOpen}
        onClose={() => {
          setIssueDetailsModalOpen(false);
          setSelectedIssueForDetails(null);
        }}
        title="Issue details"
        size="medium"
        footer={
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setIssueDetailsModalOpen(false);
              setSelectedIssueForDetails(null);
            }}
          >
            Close
          </button>
        }
      >
        {selectedIssueForDetails ? (
          <div style={{ overflowX: 'auto' }}>
            <table className="visit-table" style={{ width: '100%', marginTop: 0 }}>
              <thead>
                <tr>
                  <th>Date of Awareness</th>
                  <th>Date of Issue</th>
                  <th>Issue Category</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    {formatDateDDMMMYYYY(
                      selectedIssueForDetails.dateOfAwareness ?? selectedIssueForDetails.DateOfAwareness
                    )}
                  </td>
                  <td>
                    {formatDateDDMMMYYYY(
                      selectedIssueForDetails.dateOfIssue ?? selectedIssueForDetails.DateOfIssue
                    )}
                  </td>
                  <td>
                    {(selectedIssueForDetails.issueCategory ?? selectedIssueForDetails.IssueCategory ?? '')
                      .toString()
                      .trim() || '—'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={issueWorkflowModalOpen}
        onClose={() => {
          if (issueReplyBusyKind) return;
          setIssueWorkflowModalOpen(false);
          setSelectedIssueItem(null);
          setIssueReplyRemark('');
          setIssueReplyRemarkFieldError('');
        }}
        title={issueWorkflowMode === 'reply' ? 'Reply to Issue' : 'View Issue'}
        size="medium"
        footer={
          <>
            {issueWorkflowMode === 'reply' ? (
              <>
                <button
                  type="button"
                  className="btn btn-primary me-2"
                  disabled={issueReplyBusyKind !== null}
                  onClick={() => {
                    if (!issueReplyRemark.trim()) {
                      setIssueReplyRemarkFieldError(actionItemMessages.replyRemarkRequired);
                      return;
                    }
                    setIssueReplyRemarkFieldError('');
                    issueReplyESignIntentRef.current = 'update';
                    setShowActionItemReplyESign(true);
                  }}
                >
                  Update
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={issueReplyBusyKind !== null}
                  onClick={() => {
                    if (!issueReplyRemark.trim()) {
                      setIssueReplyRemarkFieldError(actionItemMessages.replyRemarkRequired);
                      return;
                    }
                    setIssueReplyRemarkFieldError('');
                    issueReplyESignIntentRef.current = 'finalize';
                    setShowActionItemReplyESign(true);
                  }}
                >
                  Save
                </button>
              </>
            ) : canShowIssueCloseButton(selectedIssueItem) ? (
              <button
                type="button"
                className="btn btn-success"
                disabled={actionItemClosingId === (selectedIssueItem?.actionItemID ?? selectedIssueItem?.nCRFActionItemNo)}
                onClick={() => {
                  const id = selectedIssueItem?.actionItemID ?? selectedIssueItem?.nCRFActionItemNo;
                  if (!id) return;
                  setActionItemIdToClose(id);
                  setShowActionItemCloseESign(true);
                }}
              >
                {actionItemClosingId === (selectedIssueItem?.actionItemID ?? selectedIssueItem?.nCRFActionItemNo) ? 'Closing...' : 'Close Issue'}
              </button>
            ) : null}
            <button
              type="button"
              className="btn btn-secondary"
              disabled={issueReplyBusyKind !== null}
              onClick={() => {
                setIssueWorkflowModalOpen(false);
                setSelectedIssueItem(null);
                setIssueReplyRemark('');
                setIssueReplyRemarkFieldError('');
              }}
            >
              Cancel
            </button>
          </>
        }
      >
        {selectedIssueItem && (
          <div className="create-action-item-form">
            <div className="form-group">
              <label className="form-label">Reference No</label>
              <input className="form-input" value={selectedIssueItem.actionItemNumber ?? ''} disabled />
            </div>
            <div className="form-group">
              <label className="form-label">Issue Description</label>
              <textarea className="form-input" value={selectedIssueItem.findingDescription ?? ''} disabled rows={3} />
            </div>
            <div className="form-group">
              <label className="form-label">Action Item Description {issueWorkflowMode === 'reply' ? <span className="required-asterisk">*</span> : null}</label>
              <textarea
                className="form-input"
                value={issueReplyRemark}
                onChange={(e) => {
                  setIssueReplyRemark(e.target.value);
                  setIssueReplyRemarkFieldError('');
                }}
                disabled={issueWorkflowMode !== 'reply' || issueReplyBusyKind !== null}
                rows={4}
                placeholder="Enter Action Item Description"
                aria-invalid={issueWorkflowMode === 'reply' && Boolean(issueReplyRemarkFieldError)}
              />
              {issueWorkflowMode === 'reply' && issueReplyRemarkFieldError ? (
                <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '6px' }} role="alert">
                  {issueReplyRemarkFieldError}
                </div>
              ) : null}
            </div>
          </div>
        )}
      </Modal>

      {/* E-Sign modal for Close Action Item (Action Items tab in main grid) */}
      {showActionItemCloseESign && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ElectronicSign
            show={true}
            onClose={() => {
              setShowActionItemCloseESign(false);
              setActionItemIdToClose(null);
            }}
            onSave={runAfterActionItemCloseESign}
          />
        </div>
      )}

      {/* E-Sign modal for Reply to Issue Save */}
      {showActionItemReplyESign && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ElectronicSign
            show={true}
            onClose={() => {
              issueReplyESignIntentRef.current = null;
              setShowActionItemReplyESign(false);
            }}
            onSave={runAfterActionItemReplyESign}
          />
        </div>
      )}

      {/* CRF Report Modal - rendered in portal so it appears above sidebar (avoids z-index stacking) */}
      {isReportModalOpen && reportTemplateId && createPortal(
        <Modal
          isOpen={true}
          onClose={handleCloseReportModal}
          title="Visit Report"
          size="fullscreen"
          className="crf-report-modal"
          showCloseButton={true}
        >
          <CRFForm
            key={`${reportTemplateId}-${getVisitId(reportVisit)}`}
            templateId={reportTemplateId}
            visitId={getVisitId(reportVisit)}
            visitType={reportVisit?.visitType ?? reportVisit?.VisitType ?? 'SSV'}
            onClose={handleCloseReportModal}
            onDraftSaved={handleDraftSaved}
            onSubmitSuccess={() => setToast({ type: 'success', message: commonMessages.authSuccess })}
            onApproveSendbackSuccess={() => setToast({ type: 'success', message: commonMessages.authSuccess })}
            onDcfSaveSuccess={() => setToast({ type: 'success', message: commonMessages.authSuccess })}
            embedInModal={true}
            stageVisits={isStageWithVisitsAndActions ? getVisitsByStage(activeStage) : []}
            defaultProject={currentProject || null}
            defaultSiteId={
              reportVisit?.siteID ?? reportVisit?.siteId ??
              (() => {
                const name = reportVisit?.siteName ?? reportVisit?.SiteName ?? reportVisit?.siteCode;
                if (!name) return '';
                const s = sites.find((s) => (s.siteCode ?? (s.siteName || s.sitedescription)) === name);
                return s ? (s.siteID ?? s.siteId ?? '') : '';
              })()
            }
            defaultSiteName={reportVisit?.siteName ?? reportVisit?.SiteName ?? reportVisit?.siteCode ?? ''}
            onActionItemCreated={refetchActionItems}
            readOnly={
              isViewOnly ||
              (
                // Dual Visibility Logic:
                // If we are in a Review Stage (First/Second Review)
                // BUT the visit itself is still effectively "In Progress" (or has Open DCF sent back to CRA)
                // Then the Reviewer is just "peeking" at what the CRA is doing -> Read Only.
                isStageWithVisitsAndActions && activeStage !== 'onGoing' &&
                (
                  (reportVisit?.crfStage === 'In Progress' || reportVisit?.CrfStage === 'In Progress') ||
                  (reportVisit?.hasOpenDCF === true || reportVisit?.HasOpenDCF === true)
                )
              )
            }
            reviewMode={isStageWithVisitsAndActions && activeStage !== 'onGoing'}
          />
        </Modal>,
        document.body
      )}
      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 10000 }}>
          <Alert type={toast.type} message={toast.message} onClose={() => setToast(null)} />
        </div>
      )}
    </div>
  );
};

export default VisitManagement;
